import type { GeoJSONSource, Map, MapLayerMouseEvent, MapGeoJSONFeature } from "maplibre-gl";
import type { GeoJsonDocument, GeoJsonFeatureCollection, JsonValue } from "../../core/types/geojson";
import { MAP_LAYER_IDS } from "../../config/map.config";
import { GeoJsonParser } from "../geojson/GeoJsonParser";
import { GeoJsonBounds } from "../geojson/GeoJsonBounds";

export interface GeoJsonFeatureClick {
  feature: MapGeoJSONFeature;
  lngLat: { lng: number; lat: number };
  point: { x: number; y: number };
}

const POLYGON_DEFAULTS = {
  fillColor: "#555555",
  fillOpacity: 0.2,
  strokeColor: "#000000",
  strokeOpacity: 1,
  strokeWidth: 1
} as const;

const LINE_DEFAULTS = {
  strokeColor: "#555555",
  strokeOpacity: 1,
  strokeWidth: 2
} as const;

const POINT_DEFAULTS = {
  color: "#dc2626",
  radius: 6,
  strokeColor: "#ffffff",
  strokeWidth: 1.5,
  defaultIconUrl: "./icons/default-marker.svg"
} as const;

/**
 * Renders imported GeoJSON like the original Google implementation while using
 * MapLibre layers. Original properties stay untouched. Supported styling:
 * - polygon fillColor/fill, fillOpacity, strokeColor/stroke, strokeOpacity, strokeWidth/strokeWeight;
 * - line strokeColor/stroke, strokeOpacity, strokeWidth/strokeWeight;
 * - marker labels from label.text or name, with color/fontWeight/fontSize;
 * - marker icon URLs from icon string, icon.url, or icon.path when the value is a URL/path.
 */
export class GeoJsonLayerService {
  private readonly sourceId = "imported-geojson-source";
  private readonly layerIds = [
    MAP_LAYER_IDS.importedFill,
    MAP_LAYER_IDS.importedLine,
    MAP_LAYER_IDS.importedPoint,
    MAP_LAYER_IDS.importedPointIcon,
    MAP_LAYER_IDS.importedPointLabel
  ];
  private clickHandler: ((event: MapLayerMouseEvent) => void) | null = null;
  private imageSequence = 0;

  constructor(private readonly map: Map) {}

  async setData(document: GeoJsonDocument, onFeatureClick?: (event: GeoJsonFeatureClick) => void): Promise<GeoJsonFeatureCollection> {
    const collection = GeoJsonParser.normalizeToFeatureCollection(document);
    const renderCollection = structuredClone(collection) as GeoJsonFeatureCollection;
    await this.preparePointIconImages(renderCollection);
    this.clear();

    this.map.addSource(this.sourceId, {
      type: "geojson",
      data: renderCollection as never
    });

    this.addLayers();
    if (onFeatureClick) this.bindFeatureClick(onFeatureClick);
    const bounds = GeoJsonBounds.preferredViewportFromDocument(collection);
    if (bounds) this.map.fitBounds(bounds, { padding: 70, maxZoom: 6.5, duration: 650 });
    return collection;
  }

  clear(): void {
    if (this.clickHandler) {
      for (const id of this.layerIds) {
        if (this.map.getLayer(id)) this.map.off("click", id, this.clickHandler);
      }
      this.clickHandler = null;
    }

    for (const id of [...this.layerIds].reverse()) {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    }
    if (this.map.getSource(this.sourceId)) this.map.removeSource(this.sourceId);
  }

  private addLayers(): void {
    this.map.addLayer({
      id: MAP_LAYER_IDS.importedFill,
      type: "fill",
      source: this.sourceId,
      filter: ["in", ["geometry-type"], ["literal", ["Polygon", "MultiPolygon"]]],
      paint: {
        "fill-color": ["coalesce", ["get", "fillColor"], ["get", "fill"], POLYGON_DEFAULTS.fillColor],
        "fill-opacity": ["case", ["has", "fillOpacity"], ["to-number", ["get", "fillOpacity"]], POLYGON_DEFAULTS.fillOpacity]
      }
    });

    this.map.addLayer({
      id: MAP_LAYER_IDS.importedLine,
      type: "line",
      source: this.sourceId,
      filter: ["in", ["geometry-type"], ["literal", ["LineString", "MultiLineString", "Polygon", "MultiPolygon"]]],
      paint: {
        "line-color": [
          "coalesce",
          ["get", "strokeColor"],
          ["get", "stroke"],
          ["match", ["geometry-type"], ["LineString", "MultiLineString"], LINE_DEFAULTS.strokeColor, POLYGON_DEFAULTS.strokeColor]
        ],
        "line-opacity": [
          "case",
          ["has", "strokeOpacity"],
          ["to-number", ["get", "strokeOpacity"]],
          ["match", ["geometry-type"], ["LineString", "MultiLineString"], LINE_DEFAULTS.strokeOpacity, POLYGON_DEFAULTS.strokeOpacity]
        ],
        "line-width": [
          "case",
          ["has", "strokeWidth"],
          ["to-number", ["get", "strokeWidth"]],
          ["has", "strokeWeight"],
          ["to-number", ["get", "strokeWeight"]],
          ["match", ["geometry-type"], ["LineString", "MultiLineString"], LINE_DEFAULTS.strokeWidth, POLYGON_DEFAULTS.strokeWidth]
        ]
      }
    });

    this.map.addLayer({
      id: MAP_LAYER_IDS.importedPoint,
      type: "circle",
      source: this.sourceId,
      filter: ["all", ["in", ["geometry-type"], ["literal", ["Point", "MultiPoint"]]], ["!", ["has", "__pocIconImageId"]]],
      paint: {
        "circle-color": ["coalesce", ["get", "color"], ["get", "fillColor"], ["get", "fill"], ["get", "__pocPointColor"], POINT_DEFAULTS.color],
        "circle-opacity": ["case", ["has", "fillOpacity"], ["to-number", ["get", "fillOpacity"]], 1],
        "circle-radius": ["case", ["has", "radius"], ["to-number", ["get", "radius"]], POINT_DEFAULTS.radius],
        "circle-stroke-color": ["coalesce", ["get", "strokeColor"], ["get", "stroke"], ["get", "__pocPointStrokeColor"], POINT_DEFAULTS.strokeColor],
        "circle-stroke-width": ["case", ["has", "strokeWidth"], ["to-number", ["get", "strokeWidth"]], POINT_DEFAULTS.strokeWidth]
      }
    });

    this.map.addLayer({
      id: MAP_LAYER_IDS.importedPointIcon,
      type: "symbol",
      source: this.sourceId,
      filter: ["all", ["in", ["geometry-type"], ["literal", ["Point", "MultiPoint"]]], ["has", "__pocIconImageId"]],
      layout: {
        "icon-image": ["get", "__pocIconImageId"],
        "icon-size": ["case", ["has", "iconScale"], ["to-number", ["get", "iconScale"]], 1],
        "icon-allow-overlap": true,
        "icon-ignore-placement": true
      }
    });

    this.map.addLayer({
      id: MAP_LAYER_IDS.importedPointLabel,
      type: "symbol",
      source: this.sourceId,
      filter: ["all", ["in", ["geometry-type"], ["literal", ["Point", "MultiPoint"]]], ["any", ["has", "name"], ["has", "__pocLabelText"]]],
      layout: {
        "text-field": ["coalesce", ["get", "__pocLabelText"], ["to-string", ["get", "name"]]],
        "text-size": ["case", ["has", "__pocLabelSize"], ["to-number", ["get", "__pocLabelSize"]], 12],
        "text-offset": [0, 1.2],
        "text-anchor": "top",
        "text-allow-overlap": true
      },
      paint: {
        "text-color": ["coalesce", ["get", "__pocLabelColor"], "#111827"],
        "text-halo-color": "#ffffff",
        "text-halo-width": 1
      }
    });
  }

  private bindFeatureClick(callback: (event: GeoJsonFeatureClick) => void): void {
    this.clickHandler = (event) => {
      const feature = event.features?.[0];
      if (!feature) return;
      callback({ feature, lngLat: event.lngLat, point: event.point });
    };

    for (const id of this.layerIds) {
      this.map.on("click", id, this.clickHandler);
      this.map.on("mouseenter", id, () => {
        this.map.getCanvas().style.cursor = "pointer";
      });
      this.map.on("mouseleave", id, () => {
        this.map.getCanvas().style.cursor = "";
      });
    }
  }

  private async preparePointIconImages(collection: GeoJsonFeatureCollection): Promise<void> {
    const imageLoads: Promise<void>[] = [];
    for (const feature of collection.features) {
      if (!feature.geometry || !["Point", "MultiPoint"].includes(feature.geometry.type)) continue;
      const properties = { ...(feature.properties ?? {}) } as Record<string, JsonValue>;
      this.applyLabelRenderProperties(properties);
      this.applyPointRenderProperties(properties);
      const iconUrl = this.extractRenderableIconUrl(properties) ?? POINT_DEFAULTS.defaultIconUrl;
      if (iconUrl) {
        const imageId = `poc-icon-${this.imageSequence++}`;
        properties.__pocIconImageId = imageId;
        imageLoads.push(this.loadIconImage(imageId, iconUrl));
      }
      feature.properties = properties as never;
    }
    await Promise.allSettled(imageLoads);
  }

  private applyLabelRenderProperties(properties: Record<string, JsonValue>): void {
    const label = properties.label;
    if (!label || typeof label !== "object" || Array.isArray(label)) return;
    const labelObject = label as Record<string, JsonValue>;
    const text = labelObject.text;
    const color = labelObject.color;
    const fontSize = labelObject.fontSize;
    if (typeof text === "string" && text.trim()) properties.__pocLabelText = text;
    if (typeof color === "string" && color.trim()) properties.__pocLabelColor = color;
    if (typeof fontSize === "string") {
      const numeric = Number(fontSize.replace(/px$/i, ""));
      if (Number.isFinite(numeric)) properties.__pocLabelSize = numeric;
    } else if (typeof fontSize === "number" && Number.isFinite(fontSize)) properties.__pocLabelSize = fontSize;
  }

  private applyPointRenderProperties(properties: Record<string, JsonValue>): void {
    const icon = properties.icon;
    if (!icon || typeof icon !== "object" || Array.isArray(icon)) return;
    const iconObject = icon as Record<string, JsonValue>;
    const fillColor = iconObject.fillColor;
    const strokeColor = iconObject.strokeColor;
    const scale = iconObject.scale;
    if (typeof fillColor === "string" && fillColor.trim()) properties.__pocPointColor = fillColor;
    if (typeof strokeColor === "string" && strokeColor.trim()) properties.__pocPointStrokeColor = strokeColor;
    if (typeof scale === "number" && Number.isFinite(scale)) properties.radius = Math.max(2, scale * 2);
  }

  private extractRenderableIconUrl(properties: Record<string, JsonValue>): string | null {
    const icon = properties.icon;
    if (typeof icon === "string" && this.looksLikeImageUrl(icon)) return icon;
    if (icon && typeof icon === "object" && !Array.isArray(icon)) {
      const iconObject = icon as Record<string, JsonValue>;
      for (const key of ["url", "href", "src", "path"]) {
        const value = iconObject[key];
        if (typeof value === "string" && this.looksLikeImageUrl(value)) return value;
      }
    }
    return null;
  }

  private looksLikeImageUrl(value: string): boolean {
    return /^(https?:\/\/|\.\/|\/|data:image\/|icons\/|\.\.\/).+\.(svg|png|jpg|jpeg|webp)(\?.*)?$/i.test(value) || value.startsWith("data:image/");
  }

  private async loadIconImage(imageId: string, url: string): Promise<void> {
    if (this.map.hasImage(imageId)) return;
    try {
      const response = await this.map.loadImage(url);
      if (!this.map.hasImage(imageId)) this.map.addImage(imageId, response.data);
    } catch {
      // Keep the point fallback circle if an external icon cannot be loaded.
    }
  }
}
